
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/auth";
import { checkPremiumStatusSync } from "@/App";
import { GuestGallery } from "@/components/gallery/GuestGallery";
import { UserGallery } from "@/components/gallery/UserGallery";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

const Gallery = () => {
  const { user } = useAuth();
  const isPremium = checkPremiumStatusSync();
  const { language } = useLanguage();
  
  // Ensure we have a real-time subscription for gallery changes
  useEffect(() => {
    if (!user?.id) return;
    
    // Check if any diary entry images are missing from gallery
    const syncDiaryImagesWithGallery = async () => {
      try {
        console.log("Checking for diary images not in gallery...");
        
        // Get all diary entries with images
        const { data: entries, error: entriesError } = await supabase
          .from('diary_entries')
          .select('images, user_id')
          .eq('user_id', user.id)
          .not('images', 'is', null);
        
        if (entriesError) {
          console.error("Error fetching diary entries with images:", entriesError);
          return;
        }
        
        if (!entries || entries.length === 0) {
          console.log("No diary entries with images found");
          return;
        }
        
        console.log(`Found ${entries.length} diary entries with images`);
        
        // Get all gallery photos for this user
        const { data: galleryPhotos, error: galleryError } = await supabase
          .from('gallery_photos')
          .select('url')
          .eq('user_id', user.id);
        
        if (galleryError) {
          console.error("Error fetching gallery photos:", galleryError);
          return;
        }
        
        const galleryUrls = new Set((galleryPhotos || []).map(photo => photo.url));
        
        // Find images in diary entries that aren't in the gallery
        let missingCount = 0;
        for (const entry of entries) {
          if (!entry.images) continue;
          
          const imageUrls = JSON.parse(entry.images);
          if (!Array.isArray(imageUrls)) continue;
          
          for (const imageUrl of imageUrls) {
            if (!galleryUrls.has(imageUrl)) {
              missingCount++;
              console.log(`Found image missing from gallery: ${imageUrl}`);
              
              // Add to gallery
              await supabase
                .from('gallery_photos')
                .insert({
                  title: t('gallery.photoFromDiary', language),
                  url: imageUrl,
                  user_id: user.id,
                  category: 'recent'
                });
            }
          }
        }
        
        if (missingCount > 0) {
          console.log(`Added ${missingCount} missing diary images to gallery`);
        } else {
          console.log("All diary images are present in gallery");
        }
      } catch (error) {
        console.error("Error syncing diary images with gallery:", error);
      }
    };
    
    // Run the sync on page load
    syncDiaryImagesWithGallery();
    
  }, [user, language]);
  
  // Show gallery with sample images for guest users
  if (!user) {
    return (
      <>
        <Helmet>
          <title>Baby Photo Gallery - Store Precious Moments | LittleAI Diary</title>
          <meta name="description" content="Create beautiful photo collections of your baby's growth and milestones. Store, organize and share precious memories with LittleAI Diary's photo gallery." />
          <meta name="keywords" content="baby photos, milestone pictures, baby gallery, pregnancy photos, family albums, baby memories, newborn photos" />
          <link rel="canonical" href="https://littleaidiary.com/gallery" />
          <meta property="og:title" content="Baby Photo Gallery - Store Precious Moments | LittleAI Diary" />
          <meta property="og:description" content="Create beautiful photo collections of your baby's growth and milestones. Store, organize and share precious memories." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://littleaidiary.com/gallery" />
          <meta property="og:image" content="https://lovable.dev/opengraph-image-p98pqg.png" />
        </Helmet>
        <Header />
        <GuestGallery />
        <Footer />
      </>
    );
  }

  // User is logged in, show user gallery (with premium features if premium)
  return (
    <main className="min-h-screen pt-24 pb-16">
      <Helmet>
        <title>{t('gallery.title', language)} - Store and Share Baby Photos | LittleAI Diary</title>
        <meta name="description" content="Organize and cherish your baby's photos in your personal gallery. Track growth milestones, pregnancy journey, and special moments in one secure place." />
        <meta name="keywords" content="baby photos, milestone pictures, baby gallery, pregnancy photos, family albums, baby development, growth tracking" />
        <link rel="canonical" href="https://littleaidiary.com/gallery" />
        <meta property="og:title" content={`${t('gallery.title', language)} - LittleAI Diary`} />
        <meta property="og:description" content="Organize and cherish your baby's photos in your personal gallery. Track growth milestones and special moments." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://littleaidiary.com/gallery" />
        <meta property="og:image" content="https://lovable.dev/opengraph-image-p98pqg.png" />
      </Helmet>
      <Header />
      <UserGallery userId={user.id} isPremium={isPremium} />
      <Footer />
    </main>
  );
};

export default Gallery;
